import {
  compareQuotes,
  PLACEMENT_LABEL,
  PLACEMENT_STATUSES,
  PLACEMENT_TONE,
  type InsurerFormSummary,
  type PlacementQuote,
  type PlacementStatus,
  type ReadinessStatus,
} from '@cyberscore/contracts';
import {
  ChevronRight,
  Download,
  FileText,
  ListChecks,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { href } from '../../lib/router';
import { can, useSession } from '../../lib/session';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { useAssessment } from '../assessments/api';
import { downloadInsurerPdf, useInsurerForms, usePlace } from './api';

/** Exact money, as quotes are written: RM1,500,000. */
export const money = (n: number) => `RM${n.toLocaleString('en-MY')}`;

const READINESS: Record<ReadinessStatus, [string, 'ok' | 'warn' | 'bad' | 'neutral']> = {
  ready: ['Ready to send', 'ok'],
  answers_needed: ['Answers needed', 'warn'],
  referral: ['Referred to underwriters', 'warn'],
  blocked: ['No quote', 'bad'],
  not_eligible: ['Outside this form', 'bad'],
};
const ELIGIBILITY = { eligible: ['Eligible', 'ok'], to_confirm: ['To confirm', 'warn'], ineligible: ['Not eligible', 'bad'] } as const; // prettier-ignore
const EFFECT = { refer: 'Refers', decline: 'Declines', terms: 'Terms' } as const;

export function PlacementPill({ status }: { status: PlacementStatus }) {
  return <span className={`pill ${PLACEMENT_TONE[status]}`}>{PLACEMENT_LABEL[status]}</span>;
}

/**
 * The insurers for one client (tasks 90–93): each chosen form's fit, what it still needs and what changes
 * the quote, the filled PDF and submission pack, where the client stands with each insurer, and their
 * quotes side by side.
 */
export function InsurersPage({
  id,
  embedded = false,
}: {
  id: string;
  /** Inside Client 360's Insurers tab: no crumbs or heading. */ embedded?: boolean;
}) {
  const detail = useAssessment(id);
  const forms = useInsurerForms(id);
  const { tenant } = useSession();
  const edits = can(tenant, 'clients.edit');
  const [editing, setEditing] = useState<InsurerFormSummary | null>(null);

  if (detail.isPending || forms.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || forms.isError)
    return (
      <div className="empty">
        <strong>Could not load the insurers</strong>
        {(detail.error ?? forms.error)?.message}
      </div>
    );
  const a = detail.data;
  const chosen = forms.data.filter((f) => f.chosen);
  const others = forms.data.filter((f) => !f.chosen);
  const placed = chosen.flatMap((f) => (f.placement ? [{ ...f.placement, insurer: f.insurer }] : []));
  const comparison = compareQuotes(placed);

  const change = (
    <a className="btn" href={href.answer(id, 'UW-FORMS')}>
      <ListChecks size={16} /> Change forms
    </a>
  );
  return (
    <>
      {embedded ? (
        <div className="row-between mb">
          <p className="muted">
            What each insurer’s form needs, and where the client stands with each insurer.
          </p>
          {change}
        </div>
      ) : (
        <>
          <div className="crumbs">
            <a href={href.clients()}>Clients</a>
            <ChevronRight size={14} />
            <a href={href.assessment(id)}>{a.customer_name}</a>
            <ChevronRight size={14} />
            Insurers
          </div>
          <div className="page-head row-between">
            <div>
              <h1>{a.customer_name}</h1>
              <p>What each insurer’s form needs, and where the client stands with each insurer.</p>
            </div>
            {change}
          </div>
        </>
      )}

      {chosen.length === 0 ? (
        <section className="card">
          <div className="empty">
            <FileText size={28} />
            <strong>No insurer forms chosen yet</strong>
            Choose the insurers’ forms this client goes on. The forms it fits are listed below.
          </div>
        </section>
      ) : (
        <div className="stack">
          {chosen.map((f) => (
            <FormCard
              key={f.key}
              id={id}
              f={f}
              customer={a.customer_name}
              onEdit={edits ? () => setEditing(f) : null}
            />
          ))}
        </div>
      )}

      {comparison.limits.length > 0 && (
        <section className="card mt" aria-labelledby="compare-head">
          <div className="card-head">
            <h2 className="card-title" id="compare-head">
              Quotes side by side
            </h2>
          </div>
          <div className="table-wrap">
            <table className="grid quote-compare">
              <thead>
                <tr>
                  <th>Insurer</th>
                  {comparison.limits.map((l) => (
                    <th key={l} className="num">
                      {money(l)} limit
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((r) => (
                  <tr key={r.form_key}>
                    <th scope="row">
                      {r.insurer} <PlacementPill status={r.status} />
                    </th>
                    {comparison.limits.map((l) => {
                      const c = r.cells[l];
                      return (
                        <td key={l} className="num">
                          {c ? (
                            <>
                              <strong>{money(c.premium)}</strong>
                              <span className="muted small"> · deductible {money(c.deductible)}</span>
                              {c.lowest && <span className="pill ok">Lowest</span>}
                              {c.bound && <span className="pill ok">Bound</span>}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="card mt" aria-labelledby="others-head">
          <div className="card-head">
            <h2 className="card-title" id="others-head">
              Other forms
            </h2>
          </div>
          <div className="table-wrap">
            <table className="grid">
              <tbody>
                {others.map((f) => (
                  <tr key={f.key}>
                    <th scope="row">
                      {f.insurer}
                      <div className="muted small">{f.product}</div>
                    </th>
                    <td>
                      <span className={`pill ${ELIGIBILITY[f.eligibility.status][1]}`}>
                        {ELIGIBILITY[f.eligibility.status][0]}
                      </span>
                      {f.eligibility.proposed && <span className="muted small"> · fits this client</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && <PlacementDrawer id={id} f={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function FormCard({
  id,
  f,
  customer,
  onEdit,
}: {
  id: string;
  f: InsurerFormSummary;
  customer: string;
  onEdit: (() => void) | null;
}) {
  // prettier-ignore
  const toast = useToast();
  const [busy, setBusy] = useState<'form' | 'pack' | null>(null);
  const [label, tone] = READINESS[f.readiness.status];
  const p = f.placement;
  const download = async (annex: boolean) => {
    setBusy(annex ? 'pack' : 'form');
    try {
      await downloadInsurerPdf(id, f, customer, annex);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      toast(
        code === 'no_template'
          ? `Upload ${f.insurer}’s blank form first, in Settings › Insurer forms`
          : code === 'other_edition'
            ? `The blank form uploaded isn’t the edition of ${f.form_code} that was mapped`
            : (e as Error).message,
        'bad',
      );
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card insurer-card" aria-label={f.insurer}>
      <div className="card-head row-between">
        <div>
          <h2 className="card-title">{f.insurer}</h2>
          <p className="muted small">
            {f.product} · <span className="mono">{f.form_code}</span>
          </p>
        </div>
        {p ? <PlacementPill status={p.status} /> : <span className="pill neutral">Not yet sent</span>}
      </div>
      <div className="insurer-facts">
        <Fact label="Fit">
          <span className={`pill ${ELIGIBILITY[f.eligibility.status][1]}`}>
            {ELIGIBILITY[f.eligibility.status][0]}
          </span>
        </Fact>
        <Fact label="Readiness">
          <span className={`pill ${tone}`}>{label}</span>{' '}
          <span className="muted small">{Math.round(f.readiness.completeness * 100)}% answered</span>
        </Fact>
        {f.readiness.quote && (
          <Fact label="Printed premium">
            from {money(Math.min(...f.readiness.quote.rows.map((r) => r.premium)))}
          </Fact>
        )}
        {p?.quotes.length ? (
          <Fact label="Quoted">
            {p.quotes.map((q) => `${money(q.premium)} for ${money(q.limit)}`).join(' · ')}
          </Fact>
        ) : null}
        {p?.bound && (
          <Fact label="Policy">
            <span className="mono">{p.bound.policy_number}</span> from{' '}
            {p.bound.inception.split('-').reverse().join('/')}
          </Fact>
        )}
      </div>
      {f.readiness.blockers.length > 0 && (
        <ul className="insurer-blockers">
          {f.readiness.blockers.map((b) => (
            <li key={b.key}>
              <TriangleAlert size={14} />
              <span>
                <strong>{EFFECT[b.effect]}:</strong> {b.label}. <span className="muted">{b.note}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {f.readiness.missing.length > 0 && (
        <p className="muted small">
          Still to answer:{' '}
          {f.readiness.missing
            .slice(0, 4)
            .map((m) => m.label)
            .join('; ')}
          {f.readiness.missing.length > 4 && ` and ${f.readiness.missing.length - 4} more`}.
        </p>
      )}
      <div className="row gap mt">
        {onEdit && (
          <button type="button" className="btn btn-primary btn-sm" onClick={onEdit}>
            <Pencil size={14} /> Update placement
          </button>
        )}
        {f.pdf && (
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!!busy}
              onClick={() => void download(false)}
            >
              <Download size={14} /> {busy === 'form' ? 'Filling…' : 'Filled form'}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!!busy}
              onClick={() => void download(true)}
            >
              <Download size={14} /> {busy === 'pack' ? 'Filling…' : 'Submission pack'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="muted small">{label}</div>
      <div>{children}</div>
    </div>
  );
}

type QuoteRow = { limit: string; deductible: string; premium: string; note: string };
const toRow = (q: PlacementQuote): QuoteRow => ({ limit: String(q.limit), deductible: String(q.deductible), premium: String(q.premium), note: q.note ?? '' }); // prettier-ignore
const amount = (s: string) => Number(s.replace(/[,\s]|RM/gi, ''));
const today = () => new Date().toISOString().slice(0, 10);

/** Where the client stands with one insurer: the stage, the quotes given and the one bound. */
function PlacementDrawer({ id, f, onClose }: { id: string; f: InsurerFormSummary; onClose: () => void }) {
  const p = f.placement;
  const save = usePlace(id, f.key);
  const toast = useToast();
  const uid = useId();
  const [status, setStatus] = useState<PlacementStatus>(p?.status ?? 'submitted');
  const [quotes, setQuotes] = useState<QuoteRow[]>(p?.quotes.map(toRow) ?? []);
  const [boundQuote, setBoundQuote] = useState(p?.bound?.quote ?? 0);
  const [policy, setPolicy] = useState(p?.bound?.policy_number ?? '');
  const [inception, setInception] = useState(p?.bound?.inception ?? today());
  const [note, setNote] = useState(p?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const takesQuotes = status !== 'preparing' && status !== 'submitted';
  const set = (i: number, k: keyof QuoteRow, v: string) => setQuotes((qs) => qs.map((q, j) => (j === i ? { ...q, [k]: v } : q))); // prettier-ignore

  const submit = () => {
    setError(null);
    const parsed = (takesQuotes ? quotes : []).map((q) => ({ limit: amount(q.limit), deductible: amount(q.deductible || '0'), premium: amount(q.premium), ...(q.note.trim() ? { note: q.note.trim() } : {}) })); // prettier-ignore
    if (parsed.some((q) => !(q.limit > 0) || Number.isNaN(q.deductible) || !(q.premium >= 0)))
      return setError('Give each quote a limit, a deductible and a premium in RM.');
    save.mutate(
      {
        status,
        quotes: parsed,
        ...(status === 'bound'
          ? { bound: { quote: boundQuote, policy_number: policy.trim(), inception } }
          : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        revision: p?.revision ?? 0,
      },
      {
        onSuccess: () => {
          toast(`${f.insurer}: ${PLACEMENT_LABEL[status].toLowerCase()}`, 'ok');
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError && e.issues.length ? e.issues.map((i) => i.message).join(' ') : e.message), // prettier-ignore
      },
    );
  };

  return (
    <Drawer
      title={`Placement with ${f.insurer}`}
      subtitle={f.product}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : 'Save placement'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor={`${uid}-status`}>Stage</label>
        <select
          id={`${uid}-status`}
          value={status}
          onChange={(e) => setStatus(e.target.value as PlacementStatus)}
        >
          {PLACEMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {PLACEMENT_LABEL[s]}
            </option>
          ))}
        </select>
      </div>
      {takesQuotes && (
        <fieldset className="field quote-rows">
          <legend>Quotes from {f.insurer} (RM, before taxes)</legend>
          {quotes.map((q, i) => (
            <div className="quote-row" key={i}>
              <input
                aria-label={`Quote ${i + 1} limit`}
                inputMode="numeric"
                placeholder="Limit"
                value={q.limit}
                onChange={(e) => set(i, 'limit', e.target.value)}
              />
              <input
                aria-label={`Quote ${i + 1} deductible`}
                inputMode="numeric"
                placeholder="Deductible"
                value={q.deductible}
                onChange={(e) => set(i, 'deductible', e.target.value)}
              />
              <input
                aria-label={`Quote ${i + 1} premium`}
                inputMode="numeric"
                placeholder="Premium"
                value={q.premium}
                onChange={(e) => set(i, 'premium', e.target.value)}
              />
              <input
                aria-label={`Quote ${i + 1} note`}
                placeholder="Terms or subjectivities"
                value={q.note}
                onChange={(e) => set(i, 'note', e.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                aria-label={`Remove quote ${i + 1}`}
                onClick={() => setQuotes((qs) => qs.filter((_, j) => j !== i))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setQuotes((qs) => [...qs, { limit: '', deductible: '', premium: '', note: '' }])}
          >
            <Plus size={14} /> Add a quote
          </button>
        </fieldset>
      )}
      {status === 'bound' && (
        <>
          <div className="field">
            <label htmlFor={`${uid}-bound`}>Quote bound</label>
            <select
              id={`${uid}-bound`}
              value={boundQuote}
              onChange={(e) => setBoundQuote(Number(e.target.value))}
            >
              {quotes.map((q, i) => (
                <option key={i} value={i}>
                  {`Quote ${i + 1}: ${q.limit ? money(amount(q.limit)) : '…'} limit, ${q.premium ? money(amount(q.premium)) : '…'}`}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${uid}-policy`}>Policy number</label>
            <input id={`${uid}-policy`} value={policy} onChange={(e) => setPolicy(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`${uid}-inception`}>Cover starts</label>
            <input
              id={`${uid}-inception`}
              type="date"
              value={inception}
              onChange={(e) => setInception(e.target.value)}
            />
            <span className="hint">
              The client’s record gets the policy, its premium and limit, and the renewal date a year on.
            </span>
          </div>
        </>
      )}
      <div className="field">
        <label htmlFor={`${uid}-note`}>Note</label>
        <textarea id={`${uid}-note`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {error && <p className="error">{error}</p>}
    </Drawer>
  );
}
